import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { createWorkspace } from "@/apps/rag/api/rag-api";
import { CreateWorkspaceDialog } from "@/apps/rag/components/CreateWorkspaceDialog";
import { useRagNavConfig } from "@/apps/rag/hooks/useRagData";
import { useRefreshRagNav } from "@/apps/rag/hooks/RagNavRefreshContext";
import {
  setLastKmWorkspaceId,
  workspaceHomePath,
  type RagNavWorkspaceSection,
} from "@/apps/rag/rag-nav";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getActiveApp } from "@/lib/apps";

function parseKmWorkspaceIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/rag\/workspaces\/([^/]+)/);
  return match?.[1];
}

function resolveKmActiveWorkspaceId(
  pathname: string,
  configActiveId: string | undefined,
  sections: RagNavWorkspaceSection[] | undefined,
): string | undefined {
  const candidate = configActiveId ?? parseKmWorkspaceIdFromPath(pathname);
  if (!candidate || !sections?.length) return undefined;
  const exists = sections.some((section) => section.items.some((item) => item.id === candidate));
  return exists ? candidate : undefined;
}

export function KmBreadcrumbWorkspaceSwitcher() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const refreshRagNav = useRefreshRagNav();
  const ragNavConfig = useRagNavConfig();
  const [menuOpen, setMenuOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);

  const isRag = getActiveApp(pathname)?.id === "rag";
  const sections = ragNavConfig?.workspaceSections;
  const showNewWorkspace = Boolean(ragNavConfig?.showNewWorkspace);
  const showSwitcher = isRag && Boolean(sections?.length || showNewWorkspace);

  const activeWorkspaceId = resolveKmActiveWorkspaceId(
    pathname,
    ragNavConfig?.activeWorkspaceId,
    sections,
  );

  useEffect(() => {
    if (activeWorkspaceId) {
      setLastKmWorkspaceId(activeWorkspaceId);
    }
  }, [activeWorkspaceId]);

  if (!showSwitcher) return null;

  return (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        {sections?.length ? (
          <Select
            open={menuOpen}
            onOpenChange={setMenuOpen}
            value={activeWorkspaceId}
            onValueChange={(workspaceId) => {
              setLastKmWorkspaceId(workspaceId);
              navigate(workspaceHomePath(workspaceId));
            }}
          >
            <SelectTrigger className="h-8 w-auto max-w-[220px] gap-2 px-2 py-0 text-sm focus:ring-0 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:!block [&>span]:truncate [&>span]:!text-left">
              <SelectValue placeholder="Select workspace" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              className="max-h-[min(20rem,var(--radix-select-content-available-height))]"
            >
              {sections.map((section) => (
                <SelectGroup key={section.label}>
                  <SelectLabel className="pl-2">{section.label}</SelectLabel>
                  {section.items.map((item) => (
                    <SelectItem key={item.id} value={item.id} textValue={item.label}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              {showNewWorkspace ? (
                <div className="sticky bottom-0 border-t border-[rgba(15,23,42,0.09)] bg-white px-2 py-2 dark:border-white/10 dark:bg-slate-900">
                  <button
                    type="button"
                    className="inline-flex h-8 w-full items-center justify-start gap-2 rounded-lg border border-[var(--outline-border)] px-2 text-sm text-[#101828] transition-colors hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] dark:border-white/10 dark:text-white dark:hover:border-violet-500/35 dark:hover:bg-white/5"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setMenuOpen(false);
                      setCreateWorkspaceOpen(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Workspace
                  </button>
                </div>
              ) : null}
            </SelectContent>
          </Select>
        ) : (
          <button
            type="button"
            aria-label="New Workspace"
            title="New Workspace"
            className="inline-flex size-8 items-center justify-center rounded-lg border border-[var(--outline-border)] text-[#101828] transition-colors hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] dark:border-white/10 dark:text-white dark:hover:border-violet-500/35 dark:hover:bg-white/5"
            onClick={() => setCreateWorkspaceOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <CreateWorkspaceDialog
        open={createWorkspaceOpen}
        onOpenChange={setCreateWorkspaceOpen}
        onCreate={async (body) => {
          const { workspace } = await createWorkspace(body);
          setLastKmWorkspaceId(workspace.id);
          refreshRagNav();
          navigate(workspaceHomePath(workspace.id));
        }}
      />
    </>
  );
}
